import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  MotifIcon,
  MotifErrorMessage,
  MotifToast,
  MotifFileUploaderItem,
  MotifFileUploader,
  MotifLabel,
  MotifButton,
  MotifSelect,
  MotifFormField,
  MotifOption,
  MotifProgressIndicator,
  MotifProgressIndicatorStep,
  MotifInput,
} from "@ey-xd/motif-react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { Stepper } from "../stepper/stepper";
import { apiQueryToUploadData } from "../../common/services/apiBaseUrlConfig";
import { getAccount } from "../../common/services/commonService";
import {
  region as PartnerRegion,
  countries as PartnerCountries,
} from "./partnerMockData";
import { useLocation } from "react-router-dom";
import useLogin from "../../common/redux/common/login/useLogin";
import useGetApplicationData from "../../common/redux/common/getApplicationData/useGetApplicationData";
import { ADMIN, EMEIA, PARTNER, UNITED_KINGDOM, USER } from "../../common/contants/constants";

import "./uploadData.scss";
import logger from "../../common/utils/logger";

export const UploadData = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState([]);
  const [marketSize, setMarketSize] = useState("");
  const [country, setCountry] = useState("");
  const [sectorId, setSectorId] = useState(0);
  const [subsectorId, setSubsectorId] = useState(0);
  const [subSubsectorId, setSubSubsectorId] = useState(0);
  const [functions, setFunctions] = useState("");
  const [region, setRegion] = useState("");
  const [config, setConfig] = useState("FDD");
  const [detailsStatus, setDetailsStatus] = useState("active");
  const [uploadStatus, setUploadStatus] = useState("disabled");
  const [isEnterDetails, setIsEnterDeatils] = useState(true);
  const [isProceedDisabled, setIsProceedDisabled] = useState(true);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showUploads, setShowUploads] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [showError, setShowError] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [role, setUserRole] = useState(USER);
  const [engagementId, setEngagementId] = useState("");
  const [engagementIdExist, setEngagementIdExist] = useState("");
  const [sslCompletency, setSslCompetency] = useState("");
  const [area, setArea] = useState("");

  const location = useLocation();
  const engagementIdList = location.state?.engagementIdList || {};

  const { loginDetail } = useLogin();
  const { getApplicationDetail, error } = useGetApplicationData();

  useEffect(() => {
    const cachedData = sessionStorage.getItem("userDataCache"); // Check session storage for cached data

    if (cachedData) {
      // If data exists in session storage, use it
      setUserData(JSON.parse(cachedData)); // Assuming the data is stored as JSON string
      setIsLoading(false); // Set loading state to false immediately when using cached data
    } else {
      // If no cached data, make the API call
      setIsLoading(true);
      if (
        getApplicationDetail &&
        Object.keys(getApplicationDetail).length > 0
      ) {
        setUserData(getApplicationDetail);
        setIsLoading(false);

        // Store fetched data in session storage for future use
        sessionStorage.setItem(
          "userDataCache", // Key for user data in session storage
          JSON.stringify(getApplicationDetail)
        );
      } else if (error) {
        navigate("/"); // Redirect if unauthorized
        setIsLoading(false); // Set loading state to false in case of error
      }
    }
  }, [getApplicationDetail, error, navigate]);

  const getRole = useCallback(() => {
    try {
      const userRole = loginDetail?.userRole || USER;
      return Promise.resolve(userRole); // Resolve the Promise with the role data
    } catch (error) {
      logger.error("Error fetching role:", error);
      return Promise.resolve(USER); // Resolve the Promise with the default role
    }
  }, [loginDetail]);

  useEffect(() => {
    // Fetch the role when the component mounts
    getRole().then((fetchedRole) => {
      setUserRole(fetchedRole);
    });
  }, [getRole]);

  useEffect(() => {
    if (role === PARTNER) {
      if (
        region &&
        country &&
        projectName &&
        clientName &&
        engagementId &&
        marketSize &&
        sector &&
        subSector &&
        subSubsector &&
        sslCompletency &&
        !engagementIdExist
      ) {
        setDetailsStatus("completed");
        setIsProceedDisabled(false);
      } else {
        setDetailsStatus("active");
        setIsProceedDisabled(true);
      }
    } else {
      if (
        marketSize &&
        region &&
        country &&
        sectorId &&
        subsectorId &&
        subSubsectorId &&
        functions &&
        projectName &&
        area
      ) {
        setDetailsStatus("completed");
        setIsProceedDisabled(false);
      } else {
        setDetailsStatus("active");
        setIsProceedDisabled(true);
      }
    }
  }, [
    marketSize,
    country,
    sslCompletency,
    sectorId,
    subsectorId,
    subSubsectorId,
    functions,
    engagementId,
    region,
    projectName,
    clientName,
    area,
  ]);

  const handlePrimaryClick = () => {
    if (isEnterDetails) {
      setIsEnterDeatils(false);
      setUploadStatus("active");
      setIsUploadDisabled(true);
    } else {
      setShowModal(true);
    }
  };

  const subSector = useMemo(() => {
    if (subsectorId > 0) {
      return userData?.subSectorLists?.find((item) => {
        return item.subsectorID === subsectorId;
      })?.subSectorName;
    } else {
      return "";
    }
  }, [subsectorId]);

  const sector = useMemo(() => {
    if (sectorId > 0) {
      return userData?.sectors?.find((item) => {
        return item.sectorId === sectorId;
      })?.sectorName;
    } else {
      return "";
    }
  }, [sectorId]);

  const subSubsector = useMemo(() => {
    if (subSubsectorId > 0) {
      return userData?.subSubSectorLists?.find((item) => {
        return item.subSubSectorID === subSubsectorId;
      })?.subSubSectorName;
    } else {
      return "";
    }
  }, [subSubsectorId]);

  const onProjectNameClick = (event) => {
    const trimmedValue = event.target.value.trimStart().replace(/\s+/g, " ");
    const cleanedValue = trimmedValue.replace(/[^a-zA-Z0-9 ]/g, "");
    setProjectName(cleanedValue);
  };
  const onClientNameClick = (event) => {
    const trimmedValue = event.target.value.trimStart().replace(/\s+/g, " ");
    const cleanedValue = trimmedValue.replace(/[^a-zA-Z0-9-&. ]/g, "");
    setClientName(cleanedValue);
  };
  const onEngagementIdClick = (event) => {
    const trimmedValue = event.target.value.trimStart().replace(/\s+/g, " ");
    const cleanedValue = trimmedValue.replace(/[^0-9]/g, "");
    setEngagementId(cleanedValue);
    if (role === PARTNER) {
      checkEngagementIdRecordExist(trimmedValue);
    }
  };
  const checkEngagementIdRecordExist = (trimmedValue) => {
    if (trimmedValue) {
      const valuesArray = Object.values(engagementIdList);
      const exists = valuesArray.some(
        (value) => value.toLowerCase() === trimmedValue.toLowerCase()
      );
      setEngagementIdExist(exists);
      logger.info("checkexist", exists);
    } else {
      setEngagementIdExist(false);
    }
  };

  const onConfirmClick = () => {
    setIsLoading(true);
    let regionX = "";
    if (country === UNITED_KINGDOM) {
      regionX = EMEIA;
    } else {
      regionX = area;
    }

    const formData = new FormData();
    formData.append("ModifiedBy", "Test");
    formData.append("SubSector", subSector);
    formData.append("SubSubSector", subSubsector);
    formData.append("EngagementId", engagementId);
    formData.append("UserID", 12);
    formData.append("Sector", sector);
    formData.append("UserEmailID", getAccount()?.username);
    formData.append("Country", country);
    formData.append("projectName", projectName);
    formData.append("clientName", clientName);
    formData.append("sectorId", sectorId);
    formData.append("subSectorId", subsectorId);
    formData.append("subSubSectorId", subSubsectorId);
    formData.append("Region", regionX);
    formData.append("NewRegion", region);
    formData.append("Area", area);
    files?.map((file) => {
      return formData.append("Files", file);
    });
    formData.append("Function", functions);
    formData.append("Config_file", config);
    formData.append("Revenue", marketSize);
    formData.append("SSLCompetancy", sslCompletency);
    formData.append("CreatedBy", getAccount()?.name);
    apiQueryToUploadData
      .post(
        role === PARTNER ? "api/partners/add" : "api/fileupload/add",
        formData
      )
      .then((response) => {
        setResult(response.data);
        logger.info("File uploaded successfully", response.data);
        setIsLoading(false);
        setUploadStatus("completed");
        setShowSuccess(true);
      })
      .catch((error) => {
        logger.error("Error uploading file", error);
        setIsLoading(false);
      });
  };

  const getSameFiles = (array1, array2, key) => {
    return array1.filter((item1) =>
      array2.some((item2) => item1[key] === item2[key])
    );
  };

  const removeCommonFiles = (array1, array2, key) => {
    return [
      ...array1.filter(
        (obj1) => !array2.some((obj2) => obj1[key] === obj2[key])
      ),
    ];
  };

  const onDrop = (selectedFiles) => {
    const matchingFiles = getSameFiles(selectedFiles, files, "name");
    const newFiles = removeCommonFiles(selectedFiles, matchingFiles, "name");
    const totalFiles = files?.length + newFiles?.length;
    if (role === PARTNER) {
      if (totalFiles <= 75) {
        newFiles.forEach((file) => {
          const extension = file?.path.substr(file?.path.lastIndexOf(".") + 1);
          if (
            extension === "pdf" ||
            extension === "csv" ||
            extension === "pptx" ||
            extension === "doc" ||
            extension === "docx" ||
            extension === "xlsx" ||
            extension === "xlsb" ||
            extension === "ppt" ||
            extension === "xlsm"
          ) {
            file["error"] = false;
          } else {
            file["error"] = true;
          }
        });
        const invalidFiles = Object.values(newFiles).some(
          (file) => file.error === true
        );
        if (invalidFiles) {
          alert(
            `Please try uploading again as one or more files are not supported`
          );
        } else {
          setFiles([...files, ...newFiles]);
          setShowUploads(true);
          setIsUploadDisabled(false);
        }
      } else {
        alert(
          "File count exceeded. You can upload maximum 75 files per upload"
        );
      }
    } else {
      if (selectedFiles?.length === 1) {
        const extension = selectedFiles[0]?.path.substr(
          selectedFiles[0]?.path.lastIndexOf(".") + 1
        );
        if (
          extension === "pdf" ||
          extension === "csv" ||
          extension === "pptx" ||
          extension === "doc" ||
          extension === "docx" ||
          extension === "xlsx" ||
          extension === "xlsb" ||
          extension === "xlsm" ||
          extension === "ppt"
        ) {
          selectedFiles[0]["error"] = false;
        } else {
          selectedFiles[0]["error"] = true;
        }
        const isUploadDisabled = Object.values(selectedFiles).some(
          (file) => file.error === true
        );
        if (isUploadDisabled) {
          alert(`Please try uploading again as this file is not supported`);
          setFiles([]);
          setShowUploads(false);
          setIsUploadDisabled(true);
        } else {
          setFiles(selectedFiles);
          setShowUploads(true);
          setIsUploadDisabled(false);
        }
      } else {
        alert("Please select a single file.");
      }
    }
  };

  const onModalClose = () => {
    setShowModal(false);
  };

  const onOKClick = () => {
    setShowModal(false);
    if (role === PARTNER) {
      navigate("/uploadedFiles");
    } else {
      setShowModal(false);
      navigate("/steps/files");
    }
  };

  const hanldeSecondaryClick = () => {
    if (isEnterDetails) {
      setShowError(true);
      setEngagementIdExist(false);
      setConfig("FDD");
      setCountry("");
      setSectorId(0);
      setSubsectorId(0);
      setSubSubsectorId(0);
      setRegion("");
      setEngagementId("");
      setFunctions("");
      setSslCompetency("");
      setMarketSize("");
      setDetailsStatus("active");
      setProjectName("");
      setClientName("");
      setIsProceedDisabled(true);
      setArea("");
    } else {
      setIsEnterDeatils(true);
      setFiles([]);
    }
  };

  return (
    <section>
      <div className="uploadData-container">
        <div className="head-wrapper">
          <MotifLabel className="heading">
            {role === PARTNER && isEnterDetails
              ? "Add Engagement"
              : "Upload Data"}
          </MotifLabel>
          <div className="button-wrapper">
            <MotifButton
              onClick={hanldeSecondaryClick}
              size="small"
              variant="secondary"
            >
              {isEnterDetails ? "Clear" : "Back"}
            </MotifButton>
            <MotifButton
              onClick={handlePrimaryClick}
              size="small"
              disabled={isEnterDetails ? isProceedDisabled : isUploadDisabled}
              variant="primary"
            >
              {isEnterDetails ? "Proceed" : "Upload"}
            </MotifButton>
          </div>
        </div>
        {role === ADMIN && <Stepper />}

        <div className="wrapper">
          <div className="progress">
            <MotifProgressIndicator variant="row">
              <MotifProgressIndicatorStep
                heading="Enter Details"
                status={detailsStatus}
              />
              <MotifProgressIndicatorStep
                heading="Upload Data"
                status={uploadStatus}
                className="second"
              />
            </MotifProgressIndicator>
          </div>
          {isEnterDetails ? (
            !isLoading ? (
              <div className="first-step">
                <MotifLabel className="form-heading">
                  {role === PARTNER
                    ? "Please complete the Engagement Profile below to process the Engagement data:"
                    : "Enter Details to Proceed"}
                </MotifLabel>
                {role === PARTNER ? (
                  <>
                    <div className="container">
                      <div className="field">
                        <>
                          <MotifLabel
                            aria-label="engagementId"
                            htmlFor="engagementId"
                          >
                            Engagement Id
                          </MotifLabel>
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                          <MotifFormField
                            aria-label="engagementId"
                            role="group"
                          >
                            <MotifInput
                              aria-label="engagementId"
                              id="engagementId"
                              label="engagementId"
                              type="text"
                              maxLength="8"
                              onChange={onEngagementIdClick}
                              value={engagementId}
                            />
                          </MotifFormField>
                          {!engagementId && showError && (
                            <MotifErrorMessage id="select-error-reactive">
                              Engagement Id is Required
                            </MotifErrorMessage>
                          )}
                          {role === PARTNER && engagementIdExist && (
                            <MotifErrorMessage id="select-error-reactive">
                              Engagement Id already exists
                            </MotifErrorMessage>
                          )}
                        </>
                      </div>
                      <div className="field">
                        <>
                          <MotifLabel
                            aria-label="projectName"
                            htmlFor="projectName"
                          >
                            Project Name
                          </MotifLabel>
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                          <MotifFormField aria-label="projectName" role="group">
                            <MotifInput
                              aria-label="projectName"
                              id="projectName"
                              label="projectName"
                              type="text"
                              maxLength="100"
                              onChange={onProjectNameClick}
                              value={projectName}
                            />
                          </MotifFormField>
                          {!projectName && showError && (
                            <MotifErrorMessage id="select-error-reactive">
                              Project Name is Required
                            </MotifErrorMessage>
                          )}
                        </>
                      </div>
                      <div className="field">
                        <>
                          <MotifLabel
                            aria-label="clientName"
                            htmlFor="clientName"
                          >
                            Client Name
                          </MotifLabel>
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                          <MotifFormField aria-label="clientName" role="group">
                            <MotifInput
                              aria-label="clientName"
                              id="clientName"
                              label="clientName"
                              type="text"
                              maxLength="100"
                              onChange={onClientNameClick}
                              value={clientName}
                            />
                          </MotifFormField>
                          {!clientName && showError && (
                            <MotifErrorMessage id="select-error-reactive">
                              Client Name is Required
                            </MotifErrorMessage>
                          )}
                        </>
                      </div>
                      <div className="field">
                        <MotifLabel>Region </MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />

                        <MotifFormField key={"region"}>
                          {userData?.regions?.length > 0 && (
                            <MotifSelect
                              key={region}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setRegion(value);
                                setCountry("");
                              }}
                              placeholder="Select"
                              value={region ? region : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {role === PARTNER
                                ? PartnerRegion.map((item, index) => {
                                    return (
                                      <MotifOption key={index} value={item}>
                                        {item}
                                      </MotifOption>
                                    );
                                  })
                                : userData?.regions?.map((item) => {
                                    return (
                                      <MotifOption
                                        key={item.regionName}
                                        value={item.regionName}
                                      >
                                        {item.regionName}
                                      </MotifOption>
                                    );
                                  })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!region && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Country </MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        <MotifFormField key={"country"}>
                          {userData?.countries?.length > 0 && (
                            <MotifSelect
                              key={country}
                              ariaLabelledBy="select-label"
                              onChange={(value) => setCountry(value)}
                              placeholder="Select"
                              value={country ? country : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {role === PARTNER
                                ? PartnerCountries.filter((item) => {
                                    return item.region === region;
                                  }).map((item) => {
                                    return (
                                      <MotifOption
                                        key={item}
                                        value={item.country}
                                      >
                                        {item.country}
                                      </MotifOption>
                                    );
                                  })
                                : userData?.countries
                                    ?.filter((country) => {
                                      return country.regionName === region;
                                    })
                                    .map((item) => {
                                      return (
                                        <MotifOption
                                          key={item.countryName}
                                          value={item.countryName}
                                        >
                                          {item.countryName}
                                        </MotifOption>
                                      );
                                    })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!country && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Sector</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {role !== PARTNER}
                        <MotifFormField key={"sector"}>
                          {userData?.sectors?.length > 0 && (
                            <MotifSelect
                              key={sectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSectorId(value);
                                setSubsectorId(0);
                                setSubSubsectorId(0);
                              }}
                              placeholder="Select"
                              value={sectorId ? sectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.sectors?.map((item) => {
                                return (
                                  <MotifOption
                                    key={item.sectorId}
                                    value={item.sectorId}
                                  >
                                    {item.sectorName}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!sectorId && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Sub- Sector</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {role !== PARTNER}
                        <MotifFormField key={"subsector"}>
                          {userData?.subSectorLists?.length > 0 && (
                            <MotifSelect
                              key={subsectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSubsectorId(value);
                                setSubSubsectorId(0);
                              }}
                              placeholder="Select"
                              value={subsectorId ? subsectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.subSectorLists
                                ?.filter((subSector) => {
                                  return subSector.sectorId === sectorId;
                                })
                                .map((item) => {
                                  return (
                                    <MotifOption
                                      key={item.subsectorID}
                                      value={item.subsectorID}
                                    >
                                      {item.subSectorName}
                                    </MotifOption>
                                  );
                                })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!subsectorId && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Sub- Sub Sector</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {role !== PARTNER}
                        <MotifFormField key={"subsubsector"}>
                          {userData?.subSubSectorLists?.length > 0 && (
                            <MotifSelect
                              key={subSubsectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSubSubsectorId(value);
                              }}
                              placeholder="Select"
                              value={subSubsectorId ? subSubsectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.subSubSectorLists
                                ?.filter((subsubSector) => {
                                  return (
                                    subsubSector.subsectorID === subsectorId
                                  );
                                })
                                .map((item) => {
                                  return (
                                    <MotifOption
                                      key={item.subSubSectorID}
                                      value={item.subSubSectorID}
                                    >
                                      {item.subSubSectorName}
                                    </MotifOption>
                                  );
                                })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!subSubsectorId && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Domain/Competency</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {role !== PARTNER}
                        <MotifFormField key={"sslCompletency"}>
                          <MotifSelect
                            key={"sslCompetencySelect"}
                            ariaLabelledBy="select-label-ssl"
                            onChange={(value) => setSslCompetency(value)}
                            placeholder="Select"
                            value={sslCompletency ? sslCompletency : ""}
                            visibleOptions={10}
                            triggerButtonProps={{
                              "aria-describedby": "select-error-reactive-ssl",
                            }}
                          >
                            {/* Hardcoded options */}
                            <MotifOption key="TCF-TD" value="TCF-TD">
                              TCF - TD
                            </MotifOption>
                            <MotifOption key="TCF-VME" value="TCF-VME">
                              TCF - VME
                            </MotifOption>
                            <MotifOption key="TCF-LA" value="TCF-LA">
                              TCF - LA
                            </MotifOption>
                            <MotifOption
                              key="EYP-Strategy"
                              value="EYP-Strategy"
                            >
                              EYP - Strategy
                            </MotifOption>
                            <MotifOption key="EYP-TSE" value="EYP-TSE">
                              EYP - TSE
                            </MotifOption>
                            <MotifOption key="EYP-TRS" value="EYP-TRS">
                              EYP - TRS
                            </MotifOption>
                          </MotifSelect>
                        </MotifFormField>
                        {!sslCompletency && showError && (
                          <MotifErrorMessage id="select-error-reactive-ssl">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Revenue Range</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {role !== PARTNER}
                        <MotifFormField key={"marketSize"}>
                          {userData?.revenueRanges?.length > 0 && (
                            <MotifSelect
                              key={marketSize}
                              ariaLabelledBy="select-label"
                              onChange={(value) => setMarketSize(value)}
                              placeholder="Select"
                              value={marketSize ? marketSize : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.revenueRanges?.map((item) => {
                                return (
                                  <MotifOption
                                    key={item.revenueRanges}
                                    value={item.range}
                                  >
                                    {item.range}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!marketSize && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="container">
                      <div className="field">
                        <MotifLabel>Area</MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        {userData?.areas?.length > 0 && (
                          <MotifFormField key={"area"}>
                            <MotifSelect
                              key={area}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setArea(value);
                                setRegion("");
                                setCountry("");
                              }}
                              placeholder="Select"
                              value={area ? area : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.areas?.map((item) => {
                                return (
                                  <MotifOption
                                    key={item.areaName}
                                    value={item.areaName}
                                  >
                                    {item.areaName}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          </MotifFormField>
                        )}
                        {!area && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>

                      <div className="field">
                        <MotifLabel>Region </MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        <MotifFormField key={"region"}>
                          {userData?.regions?.length > 0 && (
                            <MotifSelect
                              key={region}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setRegion(value);
                                setCountry("");
                              }}
                              placeholder="Select"
                              value={region ? region : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {(() => {
                                const uniqueRegions = new Set(); // Create a set to track unique region names
                                userData?.areaRegionCountryMappings
                                  ?.filter((areas) => areas.areaName === area)
                                  .forEach((item) =>
                                    uniqueRegions.add(item.regionName)
                                  ); // Add region names to the set

                                return Array.from(uniqueRegions).map(
                                  (
                                    regionName // Convert the set to an array and map over it
                                  ) => (
                                    <MotifOption
                                      key={regionName}
                                      value={regionName}
                                    >
                                      {regionName}
                                    </MotifOption>
                                  )
                                );
                              })()}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!region && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Country </MotifLabel>
                        <img
                          loading="lazy"
                          src={require("../../../src/common/images/asterisk.png")}
                          alt=""
                        />
                        <MotifFormField key={"country"}>
                          {userData?.countries?.length > 0 && (
                            <MotifSelect
                              key={country}
                              ariaLabelledBy="select-label"
                              onChange={(value) => setCountry(value)}
                              placeholder="Select"
                              value={country ? country : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.areaRegionCountryMappings
                                ?.filter((country) => {
                                  return country.regionName === region;
                                })
                                .map((item) => {
                                  return (
                                    <MotifOption
                                      key={item.countryName}
                                      value={item.countryName}
                                    >
                                      {item.countryName}
                                    </MotifOption>
                                  );
                                })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!country && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <>
                          <MotifLabel aria-label="Input" htmlFor="input">
                            Project Name
                          </MotifLabel>
                          {role !== PARTNER && (
                            <img
                              loading="lazy"
                              src={require("../../../src/common/images/asterisk.png")}
                              alt=""
                            />
                          )}
                          <MotifFormField aria-label="projectName" role="group">
                            <MotifInput
                              aria-label="projectName"
                              id="projectName"
                              label="projectName"
                              type="text"
                              maxLength="100"
                              onChange={onProjectNameClick}
                              value={projectName}
                            />
                          </MotifFormField>
                          {!projectName && showError && (
                            <MotifErrorMessage id="select-error-reactive">
                              Project Name is Required
                            </MotifErrorMessage>
                          )}
                        </>
                      </div>
                      <div className="field">
                        <>
                          <MotifLabel aria-label="Input" htmlFor="input">
                            Client Name
                          </MotifLabel>
                          {role !== PARTNER}
                          <MotifFormField aria-label="clientName" role="group">
                            <MotifInput
                              aria-label="clientName"
                              id="clientName"
                              label="clientName"
                              type="text"
                              maxLength="100"
                              onChange={onClientNameClick}
                              value={clientName}
                            />
                          </MotifFormField>
                        </>
                      </div>
                      <div className="field">
                        <MotifLabel>Sector</MotifLabel>
                        {role !== PARTNER && (
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                        )}
                        <MotifFormField key={"sector"}>
                          {userData?.sectors?.length > 0 && (
                            <MotifSelect
                              key={sectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSectorId(value);
                                setSubsectorId(0);
                                setSubSubsectorId(0);
                              }}
                              placeholder="Select"
                              value={sectorId ? sectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.sectors?.map((item) => {
                                return (
                                  <MotifOption
                                    key={item.sectorId}
                                    value={item.sectorId}
                                  >
                                    {item.sectorName}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!sectorId && role !== PARTNER && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Subsector</MotifLabel>
                        {role !== PARTNER && (
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                        )}
                        <MotifFormField key={"subsector"}>
                          {userData?.subSectorLists?.length > 0 && (
                            <MotifSelect
                              key={subsectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSubsectorId(value);
                                setSubSubsectorId(0);
                              }}
                              placeholder="Select"
                              value={subsectorId ? subsectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.subSectorLists
                                ?.filter((subSector) => {
                                  return subSector?.sectorId === sectorId;
                                })
                                .map((item) => {
                                  return (
                                    <MotifOption
                                      key={item.subsectorID}
                                      value={item.subsectorID}
                                    >
                                      {item.subSectorName}
                                    </MotifOption>
                                  );
                                })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!subsectorId && role !== PARTNER && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Sub- Sub Sector</MotifLabel>
                        {role !== PARTNER && (
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                        )}
                        <MotifFormField key={"subsubsector"}>
                          {userData?.subSubSectorLists?.length > 0 && (
                            <MotifSelect
                              key={subSubsectorId}
                              ariaLabelledBy="select-label"
                              onChange={(value) => {
                                setSubSubsectorId(value);
                              }}
                              placeholder="Select"
                              value={subSubsectorId ? subSubsectorId : 0}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.subSubSectorLists
                                ?.filter((subsubSector) => {
                                  return (
                                    subsubSector?.subsectorID === subsectorId
                                  );
                                })
                                .map((item) => {
                                  return (
                                    <MotifOption
                                      key={item.subSubSectorID}
                                      value={item.subSubSectorID}
                                    >
                                      {item.subSubSectorName}
                                    </MotifOption>
                                  );
                                })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!subSubsectorId && role !== PARTNER && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Revenue Range</MotifLabel>
                        {role !== PARTNER && (
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                        )}
                        <MotifFormField key={"marketSize"}>
                          {userData?.revenueRanges?.length > 0 && (
                            <MotifSelect
                              key={marketSize}
                              ariaLabelledBy="select-label"
                              onChange={(value) => setMarketSize(value)}
                              placeholder="Select"
                              value={marketSize ? marketSize : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.revenueRanges?.map((item) => {
                                return (
                                  <MotifOption
                                    key={item.range}
                                    value={item.range}
                                  >
                                    {item.range}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!marketSize && role !== PARTNER && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                      <div className="field">
                        <MotifLabel>Function</MotifLabel>
                        {role !== PARTNER && (
                          <img
                            loading="lazy"
                            src={require("../../../src/common/images/asterisk.png")}
                            alt=""
                          />
                        )}
                        <MotifFormField key={"function"}>
                          {userData?.functions?.length > 0 && (
                            <MotifSelect
                              key={functions}
                              ariaLabelledBy="select-label"
                              onChange={(value) => setFunctions(value)}
                              placeholder="Select"
                              value={functions ? functions : ""}
                              triggerButtonProps={{
                                "aria-describedby": "select-error-reactive",
                              }}
                            >
                              {userData?.functions?.map((item) => {
                                return (
                                  <MotifOption
                                    value={item.functionName}
                                    key={item.functionName}
                                  >
                                    {item.functionName}
                                  </MotifOption>
                                );
                              })}
                            </MotifSelect>
                          )}
                        </MotifFormField>
                        {!functions && role !== PARTNER && showError && (
                          <MotifErrorMessage id="select-error-reactive">
                            Please select an option from the dropdown!
                          </MotifErrorMessage>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <MotifProgressLoader show variant="default" />
            )
          ) : (
            <div className="second-step">
              <MotifLabel className="form-heading">
                Choose files to upload
              </MotifLabel>
              <div className="file-upload">
                <MotifFormField>
                  <MotifFileUploader
                    id="my-id"
                    label={
                      role === PARTNER
                        ? "Click to upload or drag and drop (max 75 files per upload)"
                        : "Click to upload or drag and drop"
                    }
                    onDrop={onDrop}
                    reactive
                  ></MotifFileUploader>
                  <div className="valid-format-msg">
                    PDF, DOC, DOCX, XLSX, XLSB, XLSM, CSV, PPTX or PPT
                  </div>
                </MotifFormField>
              </div>
              {showUploads &&
                files.map((file, index) => {
                  return (
                    <MotifFileUploaderItem
                      key={file.path}
                      aria-label="Item aria-label text"
                      fileName={file.path}
                      fileSize={file.size}
                      removable
                      error={file.error}
                      fileIcon={
                        <MotifIcon src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTQgMkg2Yy0xLjEgMC0xLjk5LjktMS45OSAyTDQgMjBjMCAxLjEuODkgMiAxLjk5IDJIMThjMS4xIDAgMi0uOSAyLTJWOGwtNi02em0yIDE2SDh2LTJoOHYyem0wLTRIOHYtMmg4djJ6bS0zLTVWMy41TDE4LjUgOUgxM3oiLz48L3N2Zz4=" />
                      }
                      onRemove={() => {
                        const newFiles = files.filter(
                          (item) => item.path !== file.path
                        );
                        setFiles(newFiles);
                        if (newFiles.length === 0) {
                          setIsUploadDisabled(true);
                        }
                      }}
                    />
                  );
                })}
              <Dialog
                onClose={onModalClose}
                open={showModal}
                className="custom-dialog"
              >
                <DialogTitle>Confirm</DialogTitle>

                <IconButton
                  onClick={onModalClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <DialogContent dividers className="modal-content">
                  {showSuccess &&
                    role === PARTNER &&
                    result?.result?.map((x) => {
                      return x.status === "Success" ? (
                        <MotifToast variant="success">
                          <div className="result">
                            {`${x.fileName} uploaded successfully`}
                          </div>
                        </MotifToast>
                      ) : (
                        <MotifToast variant="error">
                          <div className="result">
                            {`${x.fileName} upload failed`}
                          </div>
                        </MotifToast>
                      );
                    })}
                  {showSuccess && role === ADMIN && (
                    <MotifToast variant="success">
                      <div className="result">
                        {`File uploaded successfully`}
                      </div>
                    </MotifToast>
                  )}
                  {isLoading && <MotifProgressLoader show variant="default" />}
                  {!showSuccess && (
                    <>
                      <span>Please confirm the data upload</span>
                      <p>
                        Please click on Confirm if you want to upload file(s).
                        Please note that once you click on Confirm, file(s) will
                        be uploaded to the regional DBS storage in preparation
                        for processing by DBS.
                      </p>
                    </>
                  )}
                </DialogContent>
                <DialogActions>
                  <MotifButton
                    onClick={showSuccess ? onOKClick : onModalClose}
                    size="medium"
                    type="button"
                    variant="secondary"
                    disabled={isLoading}
                  >
                    {showSuccess ? "OK" : "Cancel"}
                  </MotifButton>
                  {!showSuccess && (
                    <MotifButton
                      onClick={onConfirmClick}
                      size="medium"
                      type="button"
                      disabled={isLoading}
                    >
                      Confirm
                    </MotifButton>
                  )}
                </DialogActions>
              </Dialog>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
